import React from 'react';
import { Link } from 'react-router-dom';
import { Observe } from 'rewire-core';
import { WithStyle } from 'rewire-ui';
import merge from 'deepmerge';
import { MuiThemeProvider, Theme } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import navigationTabsModel from './NavigationTabsModel';
import { withStyles } from '../utility/styles';
import mainPageModel from './MainPageModel';
import authService from '../services/AuthService';

const theme = (outerTheme: Theme) => ({
  ...outerTheme,
  overrides: merge(outerTheme.overrides || {}, {
    MuiTabs: {
      root: {
        minHeight: '30px',
        overflow: 'visible',
        [outerTheme.breakpoints.down('md')]: {
          position: 'static',
          left: 'unset',
        },
      },
    },
    MuiTab: {
      root: {
        borderRadius: 0,
        height: '30px',
        fontSize: '0.8125em',
        minHeight: '30px',
        padding: '0px 8px',
        [outerTheme.breakpoints.up('sm')]: {
          minWidth: '150px',
        },
        color: outerTheme.palette.navBarSecondary.light,
        fontWeight: '400',
      },
      textColorInherit: {
        transition: '.3s ease-out',
        opacity: '1',
        color: outerTheme.palette.navBarSecondary.light,
        '&:hover': {
          opacity: '1',
          background: 'none',
          backgroundColor: outerTheme.palette.dialogPrimary.light,
          color: outerTheme.palette.navBarPrimary.main,
        },
      },
    },
  }),
});

const styles = (theme: Theme) => ({
  tabRoot: {
    textTransform: 'none',
    '& > :first-child > :first-child': {
      display: 'none',
    },
  },
  tabNavIndicator: {
    height: '3px',
    backgroundColor: theme.palette.secondary.light,
  },
  tabSelected: {
    fontWeight: 500,
    color: theme.palette.secondary.light,
    '& > :first-child > :first-child': {
      display: 'inline-block',
    },
  },
  tabLabelIcon: {
    minHeight: '30px',
    padding: '0px',
  },
  tabWrapper: {
    flexDirection: 'row-reverse',
    '& > :first-child': {
      marginBottom: '0px !important',
    },
  },
});

interface ISecondaryNavigationTabsProps {}

interface ISecondaryNavigationTabsState {}

type SecondaryNavigationTabsProps = WithStyle<ReturnType<typeof styles>, ISecondaryNavigationTabsProps>;

class SecondaryNavigationTabs extends React.Component<SecondaryNavigationTabsProps, ISecondaryNavigationTabsState> {
  constructor(props: SecondaryNavigationTabsProps) {
    super(props);
  }

  renderPlanTabs = (props: { classes: any; secondaryIdx: number | false }) => {
    const { classes, secondaryIdx } = props;
    const jobScheduleRoute = navigationTabsModel.getJobScheduleRoute();
    const employeeScheduleRoute = navigationTabsModel.getEmployeeScheduleRoute();
    const timeOffCalendarRoute = navigationTabsModel.getTimeOffCalendarRoute();
    const timeOffListRoute = navigationTabsModel.getTimeOffListRoute();

    return (
      <Tabs value={secondaryIdx} classes={{ indicator: classes.tabNavIndicator }}>
        <Tab
          label="Job Schedule"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={jobScheduleRoute}
        />
        <Tab
          label="Employee Schedule"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={employeeScheduleRoute}
        />
        <Tab
          label="Time Off Calendar"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={timeOffCalendarRoute}
        />
        <Tab
          label="Time Off List"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={timeOffListRoute}
        />
      </Tabs>
    );
  };

  renderPayTabs = (props: { classes: any; secondaryIdx: number | false }) => {
    const { classes, secondaryIdx } = props;
    const payRoute = navigationTabsModel.getPayRoute();
    const timecardSummaryRoute = navigationTabsModel.getTimecardSummaryRoute();

    return (
      <Tabs value={secondaryIdx} classes={{ indicator: classes.tabNavIndicator }}>
        <Tab
          label="Timecard"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={payRoute}
        />
        <Tab
          label="Summary"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={timecardSummaryRoute}
        />
      </Tabs>
    );
  };

  renderBatchTabs = (props: { classes: any; secondaryIdx: number | false }) => {
    const { classes, secondaryIdx } = props;
    const batchRoute = navigationTabsModel.getBatchRoute();
    const batchAdjustmentRoute = navigationTabsModel.getAdjustmentsBatchRoute();
    const batchSummaryRoute = navigationTabsModel.getBatchSummaryRoute();
    const adjustmentsModule = authService.evalSecurityToken('view_adjustments');
    const displayAdjustmentsFeature = adjustmentsModule && mainPageModel.batchAccess ? 'flex' : 'none';

    return (
      <Tabs value={secondaryIdx} classes={{ indicator: classes.tabNavIndicator }}>
        <Tab
          label="Create Batch"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={batchRoute}
        />
        <Tab
          label="Create Adjustments Batch"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          style={{ display: displayAdjustmentsFeature }}
          component={Link}
          to={batchAdjustmentRoute}
        />
        <Tab
          label="Summary"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={batchSummaryRoute}
        />
      </Tabs>
    );
  };

  renderReportTabs = (props: { classes: any; secondaryIdx: number | false }) => {
    const { classes, secondaryIdx } = props;
    const reportRoute = navigationTabsModel.getReportRoute();
    const reportAnalyticsRoute = navigationTabsModel.getAnalyticsRoute();

    return (
      <Tabs value={secondaryIdx} classes={{ indicator: classes.tabNavIndicator }}>
        <Tab
          label="Report"
          classes={{
            root: classes.tabRoot,
            selected: classes.tabSelected,
            labelIcon: classes.tabLabelIcon,
            wrapper: classes.tabWrapper,
          }}
          component={Link}
          to={reportRoute}
        />
        {mainPageModel.analyticsAccess ? (
          <Tab
            label="Analytics"
            classes={{
              root: classes.tabRoot,
              selected: classes.tabSelected,
              labelIcon: classes.tabLabelIcon,
              wrapper: classes.tabWrapper,
            }}
            component={Link}
            to={reportAnalyticsRoute}
          />
        ) : null}
      </Tabs>
    );
  };

  // renderTestTabs = (props: {classes: any, secondaryIdx: number | false}) => {
  //   const {classes, secondaryIdx} = props;
  //   const testRoute               = navigationTabsModel.getTestRoute();

  //   return (
  //     <Tabs value={secondaryIdx} classes={{indicator: classes.tabNavIndicator}}>
  //       <Tab label='Test' classes={{root: classes.tabRoot, selected: classes.tabSelected, labelIcon: classes.tabLabelIcon, wrapper: classes.tabWrapper}} component={Link} to={testRoute} />
  //     </Tabs>
  //   );
  // }

  renderTabs = (props: { classes: any; primaryIdx: number | false; secondaryIdx: number | false }) => {
    const { classes, primaryIdx, secondaryIdx } = props;

    switch (primaryIdx) {
      case 0:
        return <this.renderPlanTabs classes={classes} secondaryIdx={secondaryIdx} />;
      case 1:
        return <this.renderPayTabs classes={classes} secondaryIdx={secondaryIdx} />;
      case 2:
        return <this.renderBatchTabs classes={classes} secondaryIdx={secondaryIdx} />;
      case 3:
        return <this.renderReportTabs classes={classes} secondaryIdx={secondaryIdx} />;
      // case 4:
      //   return <this.renderTestTabs classes={classes} secondaryIdx={secondaryIdx} />;
      default:
        return null;
    }
  };

  render() {
    const { classes } = this.props;

    return (
      <Observe
        render={() => (
          <MuiThemeProvider theme={theme}>
            <this.renderTabs
              classes={classes}
              primaryIdx={navigationTabsModel.primaryIdx}
              secondaryIdx={navigationTabsModel.secondaryIdx}
            />
          </MuiThemeProvider>
        )}
      />
    );
  }
}

export default withStyles<ISecondaryNavigationTabsProps>(styles, SecondaryNavigationTabs);
